import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchStrategy, fetchTrades } from '../api';

const REFRESH_INTERVAL = 5000;

export default function StrategyDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const [strat, setStrat] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [stratRes, tradeRes] = await Promise.allSettled([
        fetchStrategy(name),
        fetchTrades(name, 50, true),
      ]);
      if (stratRes.status === 'fulfilled' && stratRes.value.data?.strategy) {
        setStrat(stratRes.value.data.strategy);
      }
      if (tradeRes.status === 'fulfilled') setTrades(tradeRes.value.data?.trades || []);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch {}
    setLoading(false);
  }, [name]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading && !strat) {
    return <div className="loading"><div className="spinner" /><p>Loading strategy...</p></div>;
  }

  if (!strat) {
    return (
      <div className="page-strategy-detail">
        <button className="sd-back" onClick={() => navigate('/strategies')}>← Back</button>
        <div className="dash-empty">Strategy "{name}" not found</div>
      </div>
    );
  }

  const pos = strat.positions || [];
  const events = strat.events || [];
  const params = strat.params || {};

  const dotClass = `strat-dot-${strat.state?.toLowerCase()}`;

  return (
    <div className="page-strategy-detail">
      {/* Top Nav */}
      <div className="sd-topbar">
        <button className="sd-back" onClick={() => navigate('/strategies')}>← Strategies</button>
        <span className="live-indicator">
          <span className="live-pulse" /> Live · {lastRefresh}
        </span>
      </div>

      {/* Hero Banner */}
      <div className="sd-hero">
        <div className="sd-hero-left">
          <div className="sd-hero-badge">
            <span className={`strat-state-dot ${dotClass}`} />
            <span className={`strat-state-badge strat-state-${strat.state?.toLowerCase()}`}>{strat.state}</span>
          </div>
          <h1 className="sd-hero-title">{strat.entry_type === 'opening_range' ? 'Opening Range' : 'ATM Straddle'} · {strat.index} · {strat.trading_day}</h1>
          <p className="sd-hero-desc">{strat.adjustment_points}pt adj · {strat.strike_interval}pt interval</p>
        </div>
        <div className="sd-hero-right">
          <div className={`sd-hero-pnl ${(strat.pnl_amount || 0) >= 0 ? 'positive' : 'negative'}`}>
            {(strat.pnl_amount || 0) >= 0 ? '+' : '-'}₹{Math.abs(strat.pnl_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>

      {/* Info Grid */}
      <div className="sd-info-grid">
        {[
          { label: 'Index', value: strat.index },
          { label: 'Trading Day', value: strat.trading_day },
          { label: 'Window', value: `${strat.start_time} – ${strat.exit_time}` },
          { label: 'Adjustment', value: `${strat.adjustment_points} pts` },
          { label: 'Account', value: strat.account_name || 'Paper' },
          { label: 'Mode', value: strat.paper_trading ? '📝 Paper' : '🔴 Live' },
          { label: 'Lots', value: strat.lots || params.lots || 1 },
          { label: 'Entry Strike', value: params.entry_strike || '—' },
          { label: 'SL Lower', value: (strat.sl_lower || params.sl_lower) ? (strat.sl_lower || params.sl_lower) : 'Off' },
          { label: 'SL Upper', value: (strat.sl_upper || params.sl_upper) ? (strat.sl_upper || params.sl_upper) : 'Off' },
          { label: 'Trades', value: params.closed_trades || 0 },
        ].map((item, i) => (
          <div key={i} className="sd-info-card">
            <span className="sd-info-label">{item.label}</span>
            <span className="sd-info-value">{item.value}</span>
          </div>
        ))}
      </div>

      {/* Live Parameters */}
      {(params.first_15m_high > 0 || params.first_15m_low > 0 || params.last_adjustment_nifty > 0) && (
        <section className="sd-section">
          <h2 className="sd-section-title">Live Parameters</h2>
          <div className="sd-params-row">
            {[
              { label: '15m High', value: params.first_15m_high },
              { label: '15m Low', value: params.first_15m_low },
              { label: 'Last Adjust', value: params.last_adjustment_nifty },
              { label: 'Strike Mode', value: params.strike_mode || 'ATM' },
            ].filter(p => p.value).map((p, i) => (
              <div key={i} className="sd-param-chip">
                <span className="sd-param-label">{p.label}</span>
                <span className="sd-param-value">
                  {typeof p.value === 'number' ? p.value.toLocaleString('en-IN', { minimumFractionDigits: 2 }) : p.value}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Open Positions */}
      <section className="sd-section">
        <h2 className="sd-section-title">
          Open Positions
          <span className="sd-count">{pos.length}</span>
        </h2>
        {pos.length > 0 ? (
          <div className="sd-positions-grid">
            {pos.map((p, i) => (
              <div key={i} className="sd-pos-card">
                <div className="sd-pos-header">
                  <span className="sd-pos-strike">{p.strike}</span>
                  <span className={`sd-pos-pnl ${(p.pnl || 0) >= 0 ? 'positive' : 'negative'}`}>
                    {(p.pnl || 0) >= 0 ? '+' : '-'}₹{Math.abs((p.pnl || 0) * (strat.lots || 1) * (strat.lot_size || 75)).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="sd-pos-legs">
                  <div className="sd-pos-leg sd-leg-ce">
                    <span className="sd-leg-type">CE</span>
                    <div className="sd-leg-prices">
                      <span>Entry: {p.ce_sell_price?.toFixed(2)}</span>
                      <span>Now: {p.ce_current_price?.toFixed(2)}</span>
                    </div>
                  </div>
                  <div className="sd-pos-leg sd-leg-pe">
                    <span className="sd-leg-type">PE</span>
                    <div className="sd-leg-prices">
                      <span>Entry: {p.pe_sell_price?.toFixed(2)}</span>
                      <span>Now: {p.pe_current_price?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="sd-empty">No open positions</div>
        )}
      </section>

      {/* Event Timeline */}
      <section className="sd-section">
        <h2 className="sd-section-title">
          Event Timeline
          <span className="sd-count">{events.length}</span>
        </h2>
        {events.length > 0 ? (
          <div className="sd-timeline">
            {events.slice().reverse().map((evt, i) => (
              <div key={i} className="sd-timeline-item">
                <div className={`sd-tl-dot dot-${evt.type?.toLowerCase()}`} />
                <div className="sd-tl-content">
                  <div className="sd-tl-header">
                    <span className={`sd-tl-type type-${evt.type?.toLowerCase()}`}>{evt.type}</span>
                    <span className="sd-tl-time">{evt.time}</span>
                  </div>
                  <span className="sd-tl-msg">{evt.message}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="sd-empty">No events today</div>
        )}
      </section>

      {/* Trade Log */}
      <section className="sd-section">
        <h2 className="sd-section-title">
          Trade Log
          <span className="sd-count">{trades.length}</span>
        </h2>
        {trades.length > 0 ? (
          <div className="table-wrapper">
            <table className="trade-table-v2">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Reason</th>
                  <th>Leg</th>
                  <th>Side</th>
                  <th>Fill Price</th>
                  <th>Nifty</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {trades.map((t, i) => (
                  <tr key={i} className={t.side === 'SELL' ? 'sell-row' : 'buy-row'}>
                    <td className="trade-time">{t.timestamp?.split(' ')[1] || t.timestamp}</td>
                    <td><span className={`action-badge action-${t.action?.toLowerCase()}`}>{t.action}</span></td>
                    <td className="trade-reason">{t.reason}</td>
                    <td><span className={t.option_type === 'CE' ? 'ce-type' : 'pe-type'}>{t.option_type}</span> {t.strike}</td>
                    <td className={t.side === 'SELL' ? 'sell-side' : 'buy-side'}>{t.side}</td>
                    <td className="strike-cell">{t.fill_price?.toFixed(2)}</td>
                    <td>{t.nifty_price?.toFixed(2)}</td>
                    <td><span className={`action-badge ${t.status === 'FILLED' ? 'action-enter' : 'action-exit'}`}>{t.status}{t.paper_trade ? ' (P)' : ''}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="sd-empty">No trades recorded</div>
        )}
      </section>
    </div>
  );
}
